import { useCallback, useEffect, useState } from 'react'
import { ApiError, api, downloadSdkConfig, type Server } from '../api'
import { Card, Chip, CopyButton, Empty as EmptyState, Info, Loading, Modal, expiresIn, shortDate } from '../components'
import { vocabulary } from '../vocabulary'

const s = vocabulary.servers
const terms = vocabulary.terms

/**
 * The one screen with a technical audience — so it may show a file and a code, and still never
 * explains cryptography. What a developer is told is what they must do and what they get back.
 */
export default function Servers({ accountId, canConnect }: {
  accountId: string
  canConnect: boolean
}) {
  const [servers, setServers] = useState<Server[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [env, setEnv] = useState<'sandbox' | 'prod'>('sandbox')
  const [minted, setMinted] = useState<{ token: string; expiresAt: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    let live = true
    api.servers(accountId)
      .then((data) => live && setServers(data.servers))
      .catch((failure) => live && setError(
        failure instanceof ApiError ? failure.message : s.loadError))
    return () => { live = false }
  }, [accountId])

  useEffect(() => {
    setServers(null)
    setError(null)
    return load()
  }, [load])

  async function generate() {
    setBusy(true)
    try {
      const token = await api.registrationToken(accountId, env)
      setMinted({ token: token.registration_token, expiresAt: token.expires_at })
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : s.codeError)
    } finally {
      setBusy(false)
    }
  }

  if (error && !servers) return <p className="banner stop">{error}</p>
  if (!servers) return <Loading rows={4} />

  return (
    <>
      <div className="stack" style={{ gap: 3 }}>
        <h1>{terms.senders}</h1>
        <p className="small muted">{s.subhead}</p>
      </div>

      {error && <p className="banner stop">{error}</p>}

      <Card
        title={<span>{s.connected} <Info term="server" /></span>}
        action={canConnect
          ? <button className="btn primary small" onClick={() => { setMinted(null); setConnecting(true) }}>
              {s.connectSender}
            </button>
          : undefined}
      >
        {servers.length === 0 ? (
          <EmptyState
            line={s.noSenders}
            action={canConnect
              ? <button className="btn small" onClick={() => setConnecting(true)}>{s.connectSender}</button>
              : <span className="tiny faint">{s.askOwner}</span>}
          />
        ) : (
          <div className="table-wrap">
            <table className="list">
              <thead>
                <tr><th>{terms.sender}</th><th>{s.colEnvironment}</th><th>{s.colConnected}</th><th>{s.colBy}</th></tr>
              </thead>
              <tbody>
                {servers.map((server) => (
                  <tr key={server.machine_id}>
                    <td className="primary" data-label={terms.sender}>
                      <div className="stack" style={{ gap: 1 }}>
                        <span>{server.name ?? s.unnamedSender}</span>
                        <span className="tiny faint mono">{server.machine_id}</span>
                      </div>
                    </td>
                    <td data-label={s.colEnvironment}>
                      <Chip kind={server.environment === 'prod' ? 'ok' : undefined}>
                        {server.environment === 'prod' ? s.envProduction : s.envTest}
                      </Chip>
                    </td>
                    <td data-label={s.colConnected} className="muted small">{shortDate(server.connected_at)}</td>
                    <td data-label={s.colBy} className="muted small">{server.connected_by ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {canConnect && (
        <Card title={s.settingsFile}>
          <div className="between wrap-row" style={{ gap: 12 }}>
            <p className="small muted" style={{ maxWidth: '46ch' }}>
              {s.settingsFileDesc}
            </p>
            <button
              className="btn small"
              onClick={() => {
                downloadSdkConfig(accountId).catch((failure) =>
                  setError(failure instanceof ApiError ? failure.message : s.downloadFailed))
              }}
            >
              {s.downloadSettings}
            </button>
          </div>
        </Card>
      )}

      {connecting && (
        <Modal
          title={s.connectModalTitle}
          onClose={() => { setConnecting(false); setMinted(null); load() }}
          footer={s.connectModalFooter}
        >
          <div className="stack" style={{ gap: 16 }}>
            <div className="stack" style={{ gap: 10 }}>
              <span className="label">{s.environment}</span>
              <div className="segmented" role="radiogroup" aria-label={s.environment}>
                {(['sandbox', 'prod'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={env === option}
                    className={env === option ? 'seg on' : 'seg'}
                    onClick={() => setEnv(option)}
                    disabled={!!minted}
                  >
                    {option === 'prod' ? s.envProduction : s.envTest}
                  </button>
                ))}
              </div>
              <span className="tiny faint">
                {s.testProdNote}
              </span>
              {!minted && (
                <button className="btn primary" style={{ alignSelf: 'flex-start' }} disabled={busy} onClick={generate}>
                  {s.generateCode}
                </button>
              )}
            </div>

            {minted && (
              <>
                <div className="stack" style={{ gap: 6 }}>
                  <span className="label">{s.setupCodeLabel}</span>
                  <div className="row">
                    <code className="input mono" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                      {minted.token}
                    </code>
                    <CopyButton value={minted.token} label={s.copy} />
                  </div>
                  <span className="tiny faint">{s.expiresWord} {expiresIn(minted.expiresAt)}</span>
                </div>

                <div className="stack" style={{ gap: 7, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
                  <span className="label">{s.thenOnSender}</span>
                  <pre
                    className="mono"
                    style={{
                      margin: 0, padding: 12, background: 'var(--surface-2)',
                      borderRadius: 7, overflowX: 'auto', lineHeight: 1.7,
                    }}
                  >{`export OSLP_REGISTRATION_TOKEN=${minted.token.slice(0, 12)}…
java -jar your-app.jar`}</pre>
                  <div className="row wrap-row">
                    <button
                      className="btn small"
                      onClick={() => {
                        downloadSdkConfig(accountId).catch(() =>
                          setError(s.downloadRetry))
                      }}
                    >
                      {s.downloadSettings}
                    </button>
                    <span className="tiny faint">{s.propertiesFile}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  )
}
